import {FC} from 'react'
import {Checkbox} from '@components/commons/checkbox'
import {Button} from '@components/ui/button'
import {AlertTriangleIcon, Trash01Icon} from '@components/ui/icon'
import {usePhotosUpload} from '@features/photos-upload/store.ts'
import {httpBulkDeleteUploadedImages} from '@features/photos-upload/http/photos-upload.http.ts'
import {errorHandler, raise} from '@utilities/helpers.ts'
import {useAsync} from '@hooks/useAsync.ts'
import {Spinner} from '@components/ui/spinner'
import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient.ts'
import {useURLParsedParams} from '@hooks/useURLParsedParams.ts'
import {AssignmentUrlParams} from '@features/assignment/types.ts'
import {ErrorBox} from '@components/commons/error-box'
import {useTranslation} from '@/translations/i18n.tsx'
import {Skeleton} from '@components/ui/skeleton'

export const IDLE_STEP_FILE_ROW_HEIGHT = 54

const rowClassName =
    'grid grid-cols-[minmax(0,1fr)_auto] overflow-hidden items-center gap-2 px-2 border-b border-neutral-200 h-[54px] [&:not(:hover)_button]:invisible [&:not(:hover)_button]:duration-0'

type IdleStepFileRow = {
    fileName: string
} & (
    | {
          isUploaded: true
          fileId: number
      }
    | {
          isUploaded?: false
          fileId?: undefined
      }
)
export const IdleStepFileRow: FC<IdleStepFileRow> = ({fileName, isUploaded, fileId}) => {
    const {t} = useTranslation()
    const queryClient = useQueryClient()
    const urlParams = useURLParsedParams(AssignmentUrlParams)
    const selectUploadedFiles = usePhotosUpload(store => store.selectUploadedFiles)
    const unselectUploadedFiles = usePhotosUpload(store => store.unselectUploadedFiles)
    const selectedUploadedFiles = usePhotosUpload(store => store.selectedUploadedFiles)
    const removeUploadedFiles = usePhotosUpload(store => store.removeUploadedFiles)
    const selectDropzoneFiles = usePhotosUpload(store => store.selectDropzoneFiles)
    const unselectDropzoneFiles = usePhotosUpload(store => store.unselectDropzoneFiles)
    const selectedDropzoneFiles = usePhotosUpload(store => store.selectedDropzoneFiles)
    const removeDropzoneFiles = usePhotosUpload(store => store.removeDropzoneFiles)
    const assignmentId = usePhotosUpload(store => store.assignmentId)
    const removeFile = useAsync()

    if (!urlParams.success) {
        return <ErrorBox icon={<AlertTriangleIcon />} title={t('errors:default')} />
    }

    const onRemove = async () => {
        if (isUploaded) {
            try {
                await removeFile.run(
                    httpBulkDeleteUploadedImages({
                        urlParams: {assignmentId: assignmentId ?? raise('assignmentId is nullish')},
                        params: {ids: [fileId]}
                    })
                )
                removeUploadedFiles([fileId])
            } catch (error) {
                errorHandler(error)
            }
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ASSIGNMENT, urlParams.data.code]})
        } else {
            removeDropzoneFiles([fileName])
        }
    }

    const onSelect = (isSelected: boolean) => {
        if (isUploaded) {
            isSelected ? selectUploadedFiles([fileId]) : unselectUploadedFiles([fileId])
        } else {
            isSelected ? selectDropzoneFiles([fileName]) : unselectDropzoneFiles([fileName])
        }
    }

    return (
        <div className={rowClassName}>
            <div className="flex items-center gap-2">
                <Checkbox
                    checked={
                        (isUploaded && selectedUploadedFiles.includes(fileId)) ||
                        (!isUploaded && selectedDropzoneFiles.includes(fileName))
                    }
                    id="select"
                    onCheckedChange={checked => onSelect(checked)}
                    disabled={removeFile.isLoading}
                />
                <p className="text-sm font-medium overflow-hidden whitespace-nowrap text-ellipsis">{fileName}</p>
            </div>
            <Button onClick={onRemove} variant="ghost" size="sm" shape="square" disabled={removeFile.isLoading}>
                {removeFile.isLoading ? <Spinner /> : <Trash01Icon />}
            </Button>
        </div>
    )
}

export const IdleStepFileRowSkeleton = () => {
    return (
        <div className={rowClassName}>
            <div className="flex items-center gap-2">
                <Skeleton width={20} height={20} />
                <Skeleton width={240} height={20} />
            </div>
            <Skeleton width={36} height={36} />
        </div>
    )
}
